import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { FaShieldAlt, FaPlus, FaEdit, FaTrash } from 'react-icons/fa';
import { useTitle } from '../../../shared/context/TitleContext';
import { fetchPermissions, fetchRoles, deleteRole, clearError } from '../../../redux/Slices/PermissionsSlice';
import PermissionGate from '../../../shared/components/PermissionGate';
import ConfirmModal from '../../../shared/components/ConfirmModal';
import { Link } from 'react-router-dom';

const AdminPermissions = () => {
  const { setTitle } = useTitle();
  const dispatch = useDispatch();

  const { permissions, roles, loading, error, rolesLoading, rolesError } = useSelector(state => state.permissions);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, data: null });


  useEffect(() => {
    setTitle('إدارة الصلاحيات');
    dispatch(clearError());
    dispatch(fetchPermissions());
    dispatch(fetchRoles());
  }, []);



  const handleDeleteRole = (roleId) => {
    setConfirmModal({
      isOpen: true,
      data: roleId,
      message: 'هل أنت متأكد من حذف هذا الدور؟',
      title: 'تأكيد الحذف'
    });
  };

  const executeDeleteRole = () => {
    dispatch(deleteRole(confirmModal.data));
  };


  const formatDate = (dateString) => {
    if (!dateString) return 'غير محدد';
    return new Date(dateString).toLocaleDateString('ar-EG', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="main_content">
      <div className="dashboard-container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="d-flex justify-content-between align-items-center mb-4"
        >
          <h4 className="fw-bold text-dark">إدارة الصلاحيات والأدوار</h4>
          <PermissionGate permission="roles.store">
            <Link to="/add-role">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn btn-primary"
                style={{
                  backgroundColor: 'rgb(24, 65, 148)',
                  borderColor: 'rgb(24, 65, 148)',
                  borderRadius: '10px'
                }}
              >
                <FaPlus className="mx-2" />
                إضافة دور جديد
              </motion.button>
            </Link>
          </PermissionGate>
        </motion.div>

        {/* Roles List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="card shadow-sm"
        >
          <div className="card-body p-3">

              {/* Error Messages */}
              {error && (
                <div className="alert alert-danger" role="alert">
                  {error}
                </div>
              )}

              {rolesError && (
                <div className="alert alert-danger" role="alert">
                  {rolesError}
                </div>
              )}

              {/* Loading */}
              {rolesLoading && (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">جاري التحميل...</span>
                  </div>
                </div>
              )}

              {/* Roles Table */}
              {!rolesLoading && (
                <div className="table-responsive">
                  <table className="table mb-0" style={{ backgroundColor: 'transparent' }}>
                    <thead>
                      <tr>
                        <th className="border-0 fw-semibold text-center">#</th>
                        <th className="border-0 fw-semibold text-center">اسم الدور</th>
                        <th className="border-0 fw-semibold text-center">الإجراءات</th>
                      </tr>
                    </thead>
                    <tbody>
                      {roles.length === 0 ? (
                        <tr>
                          <td colSpan="3" className="text-center py-4">
                            <div className="text-muted">
                              <FaShieldAlt className="fs-1 mb-3" />
                              <p>لا توجد أدوار</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        roles.map((role, index) => (
                          <tr key={role.id} style={{ backgroundColor: 'transparent' }}>
                            <td className="text-center">{index + 1}</td>
                            <td className="text-center">
                              <div className="fw-medium">{role.name || 'غير محدد'}</div>
                            </td>
                            <td className="text-center">
                              <div className="d-flex justify-content-center gap-2">
                                <PermissionGate permission="roles.update">
                                  <Link 
                                    to={`/edit-role/${role.id}`}
                                    onClick={() => {
                                      // حفظ بيانات الدور في sessionStorage للتعديل
                                      const roleData = {
                                        id: role.id,
                                        name_en: role.name_en || '',
                                        name_ar: role.name_ar || role.name || '',
                                        permissions: role.permissions || [],
                                        permission_id: role.permissions?.map(p => typeof p === 'object' ? p.id : p) || []
                                      };
                                      sessionStorage.setItem(`role_${role.id}`, JSON.stringify(roleData));
                                      
                                      // حفظ إضافي للبيانات الأساسية
                                      sessionStorage.setItem('selectedRole', JSON.stringify({
                                        id: role.id,
                                        name: role.name,
                                        name_ar: role.name_ar || role.name,
                                        name_en: role.name_en || '',
                                        permissions: role.permissions || []
                                      }));
                                    }}
                                  >
                                    <motion.button
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      className="btn btn-sm btn-primary"
                                      style={{
                                        backgroundColor: 'rgb(24, 65, 148)',
                                        borderColor: 'rgb(24, 65, 148)',
                                        color: 'white'
                                      }}
                                      title="تعديل"
                                    >
                                      <FaEdit />
                                    </motion.button>
                                  </Link>
                                </PermissionGate>
                                
                                <PermissionGate permission="roles.destroy">
                                  <motion.button
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleDeleteRole(role.id)}
                                    title="حذف"
                                  >
                                    <FaTrash />
                                  </motion.button>
                                </PermissionGate>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
          </div>
        </motion.div>

        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal({ isOpen: false, data: null })}
          onConfirm={executeDeleteRole}
          title="تأكيد الحذف"
          message={confirmModal.message || 'هل أنت متأكد من هذا الإجراء؟'}
          confirmText="حذف"
          cancelText="إلغاء"
          confirmButtonColor="#dc3545"
          type="danger"
        />

      </div>
    </div>
  );
};

export default AdminPermissions;

// CSS لإزالة ألوان الهوفر واللون الرمادي
const style = document.createElement('style');
style.textContent = `
  .table-row-no-hover:hover {
    background-color: transparent !important;
  }
  .table tbody tr:hover {
    background-color: transparent !important;
  }
  .table tbody tr {
    background-color: transparent !important;
  }
  .table tbody td {
    text-align: center !important;
    vertical-align: middle !important;
    width: 33.33%;
  }
  .table tbody th {
    text-align: center !important;
  }
  .table tbody td .d-flex {
    justify-content: center !important;
    align-items: center !important;
    width: 100%;
    gap: 0.5rem;
  }
  .table tbody td .d-flex .btn {
    flex-shrink: 0;
  }
`;
document.head.appendChild(style);
